import { useState, type ChangeEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import styles from "./UpdateExemptionDocumentsModal.module.css";
import { Modal } from "@/shared/modal";
import type { ProtestsExemptionsPayment } from "@/features/settings/services/protestsExemptions.service";
import { signatureService } from "@/features/protest/services/signature.services";
import { toastService } from "@/shared/toast";

type UpdateExemptionDocumentsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  record: ProtestsExemptionsPayment | null;
  onSuccess?: () => void;
};

type DocSlot = {
  id: string;
  title: string;
  documentType: string;
  documentSide: "front_side" | "back_side";
  accept: string;
  description: string;
};

const DOC_SLOTS: DocSlot[] = [
  {
    id: "dl-front",
    title: "Front of Driving License",
    documentType: "driving_licence",
    documentSide: "front_side",
    accept: "image/*,application/pdf",
    description: "Clear photo or PDF of the front of your driver's license",
  },
  {
    id: "dl-back",
    title: "Back of Driving License",
    documentType: "driving_licence",
    documentSide: "back_side",
    accept: "image/*,application/pdf",
    description: "Clear photo or PDF of the back of your driver's license",
  },
  {
    id: "deed-closing",
    title: "Deed / Closing Documents",
    documentType: "deed_closing_document",
    documentSide: "front_side",
    accept: "image/*,application/pdf",
    description: "Deed, closing disclosure, or settlement statement",
  },
];

export const UpdateExemptionDocumentsModal = ({
  isOpen,
  onClose,
  record,
  onSuccess,
}: UpdateExemptionDocumentsModalProps) => {
  const queryClient = useQueryClient();
  const [uploadingSlot, setUploadingSlot] = useState<string | null>(null);

  if (!record) return null;

  const propertyId = record.property_details?.property_id || record.propertyId || String(record.id);

  const address =
    record.property_details?.full_address ||
    record.property_details?.name ||
    record.property_details?.city ||
    `Property #${propertyId}`;

  // Find existing documents for this record
  const existingDocs = record.property_homestead_document || [];

  const getExistingDoc = (type: string, side: string) => {
    return existingDocs.find(
      (d) =>
        (d.document_type || "").toLowerCase() === type.toLowerCase() &&
        (d.document_side || "front_side").toLowerCase() === side.toLowerCase() &&
        d.document
    );
  };

  const handleFileUpload = async (slot: DocSlot, event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingSlot(slot.id);
    try {
      const res = await signatureService.uploadHomesteadDocument({
        property_id: propertyId,
        document_type: slot.documentType,
        document_side: slot.documentSide,
        file,
      });

      if (res && res.success === false) {
        toastService.show(res.message || `Failed to upload ${slot.title}.`, "error");
      } else {
        toastService.show(`${slot.title} uploaded successfully!`, "success");
        await queryClient.invalidateQueries({ queryKey: ["settings-protests-exemptions"] });
        await queryClient.invalidateQueries({ queryKey: ["settings-exemptions-count"] });
        if (onSuccess) onSuccess();
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to upload document.";
      toastService.show(msg, "error");
    } finally {
      setUploadingSlot(null);
      // Reset input value so same file can be re-selected if needed
      event.target.value = "";
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      unconstrained
      className={styles.modalCard}
      headerClassName={styles.modalHeader}
      title={
        <div className={styles.modalHeaderTitleWrap}>
          <div className={styles.modalIconBadge}>
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" strokeLinecap="round" />
              <line x1="9" y1="15" x2="15" y2="15" strokeLinecap="round" />
            </svg>
          </div>
          <div className={styles.modalHeaderInfo}>
            <span className={styles.modalEyebrow}>HOMESTEAD EXEMPTION</span>
            <h2 className={styles.modalAddress}>{address}</h2>
          </div>
        </div>
      }
      bodyClassName={styles.modalBody}
      ariaLabel={`Update exemption documents for ${address}`}
    >
      <div className={styles.infoNotice}>
        <div className={styles.noticeIcon}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </div>
        <p className={styles.noticeText}>
          You can upload or replace your driver&apos;s license and deed/closing documents at any
          time. These files will be automatically attached to your exemption filing.
        </p>
      </div>

      <div className={styles.uploadCardsList}>
        {DOC_SLOTS.map((slot) => {
          const existing = getExistingDoc(slot.documentType, slot.documentSide);
          const isUploading = uploadingSlot === slot.id;

          return (
            <div key={slot.id} className={styles.uploadCard}>
              <div className={styles.uploadCardHeader}>
                <div className={styles.docTitleGroup}>
                  <div className={styles.docIconWrap}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    >
                      <rect x="3" y="4" width="18" height="16" rx="3" />
                      <circle cx="9" cy="10" r="2" />
                      <line x1="15" y1="9" x2="17" y2="9" />
                      <line x1="15" y1="12" x2="17" y2="12" />
                    </svg>
                  </div>
                  <div className={styles.docMeta}>
                    <span className={styles.docName}>{slot.title}</span>
                    <span className={styles.docStatusText}>{slot.description}</span>
                  </div>
                </div>

                <span
                  className={`${styles.statusPill} ${
                    existing ? styles.statusPillAttached : styles.statusPillPending
                  }`}
                >
                  {existing ? "✓ Attached" : "Pending"}
                </span>
              </div>

              <div className={styles.uploadCardActions}>
                {existing?.document ? (
                  <a
                    href={existing.document}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.viewExistingLink}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    <span>View current document</span>
                  </a>
                ) : (
                  <span style={{ fontSize: "0.8rem", color: "#94a3b8" }}>No file uploaded yet</span>
                )}

                <label
                  htmlFor={`upload-slot-${slot.id}`}
                  className={`${styles.uploadBtn} ${isUploading ? styles.uploadBtnDisabled : ""}`}
                >
                  {isUploading ? (
                    "Uploading..."
                  ) : existing ? (
                    <>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      <span>Replace</span>
                    </>
                  ) : (
                    <>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      <span>Upload</span>
                    </>
                  )}
                </label>
                <input
                  id={`upload-slot-${slot.id}`}
                  type="file"
                  accept={slot.accept}
                  className={styles.hiddenInput}
                  disabled={isUploading}
                  onChange={(e) => handleFileUpload(slot, e)}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.modalFooter}>
        <button type="button" className={styles.doneBtn} onClick={onClose}>
          Done
        </button>
      </div>
    </Modal>
  );
};
